import React, { type ReactNode, useRef, useEffect, forwardRef } from 'react'
import { styled } from '@linaria/react'
import { colors } from '../styles/colors'
import { Typography } from './Typography'
import ChevronIcon from '../svgr/Chevron'

export const CollapsableBox = forwardRef<
  HTMLDetailsElement,
  {
    title: ReactNode
    children: ReactNode
    defaultExpanded?: boolean
    className?: string
  }
>(({ title, children, defaultExpanded = false, className }, forwardedRef) => {
  const internalRef = useRef<HTMLDetailsElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  // Merge refs
  useEffect(() => {
    const detailsEl = internalRef.current
    if (detailsEl && forwardedRef) {
      if (typeof forwardedRef === 'function') {
        forwardedRef(detailsEl)
      } else {
        forwardedRef.current = detailsEl
      }
    }
  }, [forwardedRef])

  useEffect(() => {
    const details = internalRef.current
    const content = contentRef.current
    if (!details || !content) {
      return
    }

    const handleToggle = () => {
      if (details.open) {
        // Opening animation
        const height = content.scrollHeight
        content.style.maxHeight = `${height}px`
      } else {
        // Closing animation
        content.style.maxHeight = '0px'
      }
    }

    // Set initial state
    if (details.open) {
      content.style.maxHeight = `${content.scrollHeight}px`
    } else {
      content.style.maxHeight = '0px'
    }

    details.addEventListener('toggle', handleToggle)
    return () => details.removeEventListener('toggle', handleToggle)
  }, [])

  return (
    <Container className={className} ref={internalRef} open={defaultExpanded}>
      <Header>
        <Typography fontSize={16} fontWeight={400} color={colors.textPrimary}>
          {title}
        </Typography>

        <ChevronContainer>
          <ChevronIcon />
        </ChevronContainer>
      </Header>

      <Content ref={contentRef}>
        <ContentInner>
          <Typography
            fontSize={14}
            fontWeight={400}
            color={colors.textSecondary}
          >
            {children}
          </Typography>
        </ContentInner>
      </Content>
    </Container>
  )
})

CollapsableBox.displayName = 'CollapsableBox'

const Container = styled.details`
  border-bottom: 1px solid ${colors.borderSecondary};
`

const Header = styled.summary`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  list-style: none; /* Remove default triangle */

  /* Remove default triangle in WebKit */
  &::-webkit-details-marker {
    display: none;
  }

  &:hover {
    opacity: 0.8;
  }
`

const ChevronContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: ${colors.textSecondary};
  transition: transform 0.2s ease;
  transform: rotate(0deg);
  flex-shrink: 0;

  details[open] & {
    transform: rotate(180deg);
  }
`

const Content = styled.div`
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.3s ease-in-out;
`

const ContentInner = styled.div`
  min-height: 0;
  padding-bottom: 16px;
`
